import React, { useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useRecoilState, useSetRecoilState } from "recoil";
import CancelButton from "../../../../../components/CancelButton";
import ConfirmBoxModal from "../../../../../components/ConfirmBoxModal";
import SaveButton from "../../../../../components/SaveButton";

import {
  addStateType,
  editStateType,
  rowEditType,
  savingType,
  typeFetching,
} from "../atoms/atoms";
import useAddType from "../hooks/useAddType";

import TypeDetails from "./TypeDetails";
import Timer from "components/Timer";
import { IType } from "../types";
import useEditType from "../hooks/useEditType";
import SubmitHookForm from "components/SubmitHookForm";

interface TypeFormProps {
  isAdd: boolean;
  setIsAdd;
}

export default function TypeForm(props: { reFetch: () => void }) {
  // Drilled Props
  const { reFetch } = props;
  // Atom States
  const [isAddState, setIsAddState] = useRecoilState(addStateType);
  const [actionEditState, setActionEditState] = useRecoilState(editStateType);
  const [isSaving, setIsSaving] = useRecoilState(savingType);
  const [editData, setEditData] = useRecoilState(rowEditType);
  const setIsFetching = useSetRecoilState(typeFetching);

  // Local States
  const [newData, setNewData] = useState<IType>();
  const [saveModal, setSaveModal] = useState(false);
  const [changes, setChanges] = useState(false);

  // Hook Functions
  const updateData = useEditType();
  const addData = useAddType();

  // Form Functions
  const useFormMethods = useForm({
    defaultValues: { ...editData },
  });

  const onSubmit = (data) => {
    const value = {
      ...data,
      typeName: data.typeName.trim(),
    };
    setNewData(value);
    setSaveModal(true);
    setChanges(false);
    setIsSaving(true);
  };

  const handleCancel = () => {
    useFormMethods.reset();
    useFormMethods.clearErrors();
    setEditData(undefined);
    setIsAddState(false);
    setActionEditState(false);
  };

  const handleSave = async () => {
    if (newData) {
      if (actionEditState) {
        updateData(newData);
      } else {
        addData(newData);
      }
    }

    await Timer(100);
    setIsFetching(true);
    setIsSaving(false);
    useFormMethods.reset();
    useFormMethods.clearErrors();
    setEditData(undefined);
    setIsAddState(false);
    setActionEditState(false);
    reFetch();
  };
  return (
    <>
      <div className="px-2 py-2 space-y-2 border-l-2 border-primary box-shadow">
        <FormProvider {...useFormMethods}>
          <form onSubmit={useFormMethods.handleSubmit(onSubmit)}>
            <div className="flex flex-row  justify-between items-center text-quinary font-bold font-sans text-lg pb-1">
              <div className="ml-2 font-bold text-primary text-base">
                {isAddState ? "Add New Type" : "Edit Type"}
              </div>
              <div className="flex justify-between gap-2 mr-2">
                <SubmitHookForm
                  type="submit"
                  disabled={!changes}
                  buttonText={isSaving ? "Saving.." : "Save"}
                  className={undefined}
                  onClick={undefined}
                />
                <CancelButton
                  onClick={(event) => {
                    event.preventDefault();
                    handleCancel();
                  }}
                />
              </div>
            </div>
            <div className="mx-2 border-b-2 border-secondary"></div>
            <div className="text-xs text-secondary italic font-normal mt-2 ml-2">
              <b> Note:</b> All fields with * are required to fill.
            </div>
          </form>
          <div>
            <TypeDetails data={editData} setChanges={setChanges} />
          </div>
        </FormProvider>
      </div>

      {saveModal && (
        <ConfirmBoxModal
          ModalTitle={isAddState ? "Add Type" : "Update Type"}
          qConfirmation={`Are you sure you want to ${
            isAddState ? "add " : "update "
          } this?`}
          isOpen={saveModal}
          onClose={() => {
            setSaveModal(false);
            setIsSaving(false);
            setChanges(true);
          }}
          onClickFunction={() => {
            setSaveModal(false);
            handleSave();
          }}
          cancelButtonRef={undefined}
        />
      )}
    </>
  );
}
